/**
 * Eigenbau: Kopfleiste des Entwurfs — 60px, Brotkrume, Akzentknopf.
 *
 * Der Entwurf setzt über den Inhalt eine ruhige Zeile: links die Brotkrume in
 * Mono, rechts der Akzentknopf. Eine Haarlinie trennt sie vom Inhalt, kein
 * Weichzeichner und keine Fläche — anders als die bisherige obere Leiste, die
 * nur auf kleinen Bildschirmen erschien und durchscheinend über dem Inhalt lag.
 *
 * Kein Tremor-Gegenstück: Die Blocksammlung hat Kopfleisten mit Logo, Suche und
 * Avatarmenü. Hier trägt die Schiene die Navigation; die Kopfleiste sagt, wo man
 * ist, öffnet die Suche und bietet die eine Handlung an.
 *
 * Das Suchfeld ist ein **Knopf**, kein Eingabefeld: Getippt wird im Suchfenster,
 * das darüber aufgeht. Ein echtes `<input>` an dieser Stelle hätte zwei
 * Eingabestellen für eine Eingabe — und die Unterstreichung des Entwurfs sähe
 * wie ein Feld aus, das den Fokus hält, ohne ihn zu haben.
 */

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { cx, focusRing } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

export function Kopfleiste({
  kuerzel,
  teile,
  zusatz,
  onSuche,
  className,
}: {
  /** Das Projektkürzel links, oder nichts bei „Alle Projekte". */
  kuerzel?: string;
  /** Der Bereich, aus `lib/brotkrume`. */
  teile: readonly string[];
  /** Das letzte Glied einer Detailseite, das aus den Daten kommt. */
  zusatz?: ReactNode;
  /** Öffnet das Suchfenster. Dieselbe Handlung wie ⌘K. */
  onSuche?: () => void;
  className?: string;
}) {
  const glieder = [...teile, ...(zusatz ? [zusatz] : [])];

  return (
    <div
      className={cx(
        "flex h-[60px] flex-none items-center gap-[22px] border-b border-linie px-9",
        className,
      )}
    >
      <nav
        aria-label="Pfad"
        className="flex min-w-0 flex-1 items-center gap-[9px]"
      >
        {kuerzel ? <Mikrolabel grad={12}>{kuerzel}</Mikrolabel> : null}
        {glieder.map((glied, i) => (
          <span key={i} className="flex min-w-0 items-center gap-[9px]">
            {(kuerzel || i > 0) && (
              <span aria-hidden="true" className="text-linie3">
                /
              </span>
            )}
            {/*
              Nur das letzte Glied steht in voller Tinte — es ist der Ort, an
              dem man ist. Die Glieder davor ordnen bloß ein.
            */}
            <Mikrolabel
              grad={12}
              ton={i === glieder.length - 1 ? "laut" : "leise"}
              className="truncate"
            >
              {glied}
            </Mikrolabel>
          </span>
        ))}
      </nav>

      {/*
        Das Suchfeld des Entwurfs: 212px, Haarlinie unten in `--linie3`, Lupe,
        „Suchen", rechts das Kürzel. Der Entwurf setzt es 7px tiefer als die
        Mitte (`margin-top: 7px`), damit die Unterstreichung auf der Grundlinie
        der Brotkrume sitzt statt darunter zu schweben.
      */}
      <button
        type="button"
        onClick={onSuche}
        aria-label="Suche öffnen"
        aria-keyshortcuts="Meta+K Control+K"
        className={cx(
          "mt-[7px] hidden w-[212px] flex-none items-center gap-2.5 border-b border-linie3 pb-[5px] text-left md:flex",
          "transition-colors duration-150 ease-aus hover:border-tinte4",
          focusRing,
        )}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="square"
          aria-hidden="true"
          className="flex-none text-tinte4"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className="flex-1 text-[14px] font-light text-tinte4">Suchen</span>
        <Mikrolabel grad={11.5} className="flex-none">
          ⌘K
        </Mikrolabel>
      </button>

      {/*
        Der Anlegedialog steht heute in `AufgabenListe` und hat seinen Zustand
        dort. Der Knopf führt deshalb dorthin, statt einen Dialog zu öffnen, den
        die Hülle nicht kennt — ein Knopf, der nichts tut, wäre schlimmer als
        einer, der einen Schritt weit führt. Mit dem Umbau der Aufgabenliste
        zieht der Dialog in die Hülle, dann öffnet er hier direkt.
      */}
      <Link
        to="/aufgaben"
        className={cx(
          "flex h-[31px] flex-none items-center bg-blue-600 px-[15px] text-[13px] font-medium text-white",
          "transition-colors duration-150 ease-aus hover:bg-blue-700 hover:text-white",
          focusRing,
        )}
      >
        Neue Aufgabe
      </Link>
    </div>
  );
}
